import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Lock, LockOpen, Clock, CheckCircle2, Loader2, CalendarClock } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useTodaysSession,
  useSessionLockStatus,
  useLockSession,
  useUnlockSession,
} from '@/hooks/useDentalSession';
import { useConvertPlanToTreatment } from '@/hooks/useTreatmentPlans';
import { fdiToHaderup } from '@/utils/toothNotation';
import { useAuth } from '@/lib/auth';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';

// ── Props ─────────────────────────────────────────────────────────────────────

interface SessionPanelProps {
  patientId: string;
  clinicId: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SessionPanel({ patientId, clinicId, className }: SessionPanelProps) {
  const { t } = useTranslation();
  const [noteDraft, setNoteDraft] = useState('');

  const today = new Date().toISOString().slice(0, 10);

  // Realtime subscription hoisted to DentalModulePage (single channel per mount).

  const { data, isLoading } = useTodaysSession(patientId, clinicId);
  const { data: lockRow, isLoading: lockLoading } = useSessionLockStatus(patientId, today);
  const lockSession = useLockSession();
  const unlockSession = useUnlockSession();
  const { user, isClinicAdmin } = useAuth();
  const canUnlock = isClinicAdmin(clinicId);
  const convertPlan = useConvertPlanToTreatment();

  const isLocked = Boolean(lockRow);

  const handleLock = async () => {
    try {
      await lockSession.mutateAsync({ patientId, sessionDate: today, notes: noteDraft || undefined });
      toast.success('Sessionen er låst');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  const handleUnlock = async () => {
    if (!lockRow) return;
    try {
      await unlockSession.mutateAsync({ lockId: lockRow.id, patientId, sessionDate: today });
      toast.success('Sessionen er låst op');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  const handleMarkCompleted = async (planId: string) => {
    try {
      await convertPlan.mutateAsync({ patientId, planId, clinicId });
      toast.success(t('dental.plan.actions.markCompleted'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  if (isLoading || lockLoading) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-ds-teal" />
      </div>
    );
  }

  const { plannedItems = [], completedToday = [], appointment } = data ?? {};

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ds-line bg-ds-card flex-shrink-0">
        <h3 className="text-sm font-semibold text-ds-text">
          {t('dental.session.title')}
        </h3>
        <div className="flex items-center gap-2">
          {isLocked && lockRow && (
            <span className="flex items-center gap-1 rounded-full bg-ds-amber-tint px-2.5 py-1 text-xs font-medium text-ds-amber-ink">
              <Lock className="h-3 w-3" />
              Sessionen er låst af {lockRow.locked_by === user?.id ? 'dig' : 'en kollega'}{' '}
              {format(new Date(lockRow.locked_at), 'HH:mm', { locale: da })}
            </span>
          )}
          {isLocked && canUnlock && lockRow && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleUnlock}
              disabled={unlockSession.isPending}
              aria-label="Lås session op"
            >
              <LockOpen />
              Lås op
            </Button>
          )}
          {!isLocked && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleLock}
              disabled={lockSession.isPending}
              aria-label="Lås session"
            >
              <Lock />
              {t('dental.session.lock')}
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto divide-y divide-ds-line bg-ds-sunk">
        {/* Section 1: Today's appointment */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted mb-2">
            {t('dental.session.todaysAppointment')}
          </p>
          {appointment ? (
            <div className="rounded-lg border border-ds-line bg-ds-card p-3">
              <div className="flex items-center gap-2 text-sm text-ds-text">
                <CalendarClock className="h-4 w-4 text-ds-teal flex-shrink-0" />
                <span className="font-medium">
                  {format(new Date(appointment.starts_at), 'HH:mm', { locale: da })}
                  {appointment.ends_at &&
                    ` – ${format(new Date(appointment.ends_at), 'HH:mm', { locale: da })}`}
                </span>
                {appointment.service_name && (
                  <span className="text-ds-muted">
                    · {appointment.service_name}
                  </span>
                )}
              </div>
              {appointment.clinician_name && (
                <p className="mt-1 text-xs text-ds-muted">
                  {appointment.clinician_name}
                </p>
              )}
              {appointment.notes && (
                <p className="mt-1 text-xs text-ds-muted italic">
                  {appointment.notes}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-ds-muted italic">
              Ingen aftale i dag
            </p>
          )}
        </div>

        {/* Section 2: Today's planned items */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted mb-2">
            {t('dental.session.todaysPlan')}
            {plannedItems.length > 0 && (
              <span className="ml-1 text-ds-placeholder">
                ({plannedItems.length})
              </span>
            )}
          </p>
          {plannedItems.length === 0 ? (
            <p className="text-sm text-ds-muted italic">
              Ingen planlagte elementer for i dag
            </p>
          ) : (
            <div className="space-y-1.5">
              {plannedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-lg border border-ds-line bg-ds-card px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono font-semibold text-ds-teal mr-1.5">
                      {item.tooth_fdi}
                    </span>
                    <span className="text-xs font-mono text-ds-muted mr-1.5">
                      {item.treatment_type}
                    </span>
                    <span className="text-sm text-ds-text truncate">
                      {item.treatment_description}
                    </span>
                  </div>
                  {!isLocked && (
                    <Button
                      type="button"
                      className="shrink-0"
                      onClick={() => handleMarkCompleted(item.id)}
                      disabled={convertPlan.isPending}
                    >
                      <CheckCircle2 />
                      {t('dental.plan.actions.markCompleted')}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 3: Completed today */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted mb-2">
            {t('dental.session.doneToday')}
            {completedToday.length > 0 && (
              <span className="ml-1 text-ds-placeholder">
                ({completedToday.length})
              </span>
            )}
          </p>
          {completedToday.length === 0 ? (
            <p className="text-sm text-ds-muted italic">
              Ingen behandlinger registreret i dag
            </p>
          ) : (
            <div className="space-y-1.5">
              {completedToday.map((treatment) => (
                <div
                  key={treatment.id}
                  className="flex items-center gap-3 rounded-lg border border-ds-green-line bg-ds-green-tint px-3 py-2"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-ds-green flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono font-semibold text-ds-muted mr-1.5">
                      {treatment.tooth_fdi}
                    </span>
                    <span className="text-xs font-mono text-ds-muted mr-1.5">
                      ({fdiToHaderup(treatment.tooth_fdi)})
                    </span>
                    <span className="text-sm text-ds-text truncate">
                      {treatment.treatment_description}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-ds-muted flex-shrink-0">
                    <Clock className="h-3 w-3" />
                    {format(new Date(treatment.created_at), 'HH:mm', { locale: da })}
                  </div>
                  {treatment.actual_cost != null && (
                    <span className="text-xs font-medium text-ds-muted flex-shrink-0">
                      {treatment.actual_cost.toLocaleString('da-DK')} kr
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Session notes */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted mb-2">
            Note
          </p>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            disabled={isLocked}
            placeholder={t('dental.session.noteDraftPlaceholder')}
            rows={3}
            className={cn(
              'w-full rounded-lg border border-ds-line bg-ds-card',
              'px-3 py-2 text-sm text-ds-text placeholder:text-ds-muted',
              'focus:outline-none focus:ring-2 focus:ring-ds-teal resize-none',
              isLocked && 'opacity-60 cursor-not-allowed',
            )}
          />
        </div>
      </div>
    </div>
  );
}
